import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { CircleNotch, Check, ArrowLeft, ArrowRight, Briefcase } from "@phosphor-icons/react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../../../components/ui/dialog"
import { Button } from "../../../../components/ui/button"
import { Input } from "../../../../components/ui/input"
import { Label } from "../../../../components/ui/label"
import { ContactSearch, UserSearch } from "../../../../components/ui/collection-search"
import { useDivisions } from "../../../../hooks/useDivisions"
import {
  useCreateMatter,
  useWorkflows,
  useWorkflowContacts,
  useWorkflowRoles,
  type CreateMatterParty,
  type CreateMatterRole,
} from "../../hooks/useCreateMatter"
import { cn } from "../../../../lib/utils"

type WizardStep = 1 | 2 | 3 | 4 | 5

interface Division {
  _id: string
  name: string
}

interface Workflow {
  _id: string
  name: string
}

interface WorkflowContact {
  _id: string
  name: string
  main_contact?: boolean
}

interface WorkflowRole {
  _id: string
  name: string
}

interface CreateMatterModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateMatterModal({ open, onOpenChange }: CreateMatterModalProps) {
  const navigate = useNavigate()
  const createMatter = useCreateMatter()

  // Wizard state
  const [step, setStep] = useState<WizardStep>(1)
  const [error, setError] = useState<string | null>(null)

  // Form state
  const [selectedDivision, setSelectedDivision] = useState<Division | null>(null)
  const [selectedWorkflow, setSelectedWorkflow] = useState<Workflow | null>(null)
  const [parties, setParties] = useState<Record<string, string>>({}) // workflow_contact_id -> contact_id
  const [roles, setRoles] = useState<Record<string, string>>({}) // workflow_role_id -> user_id
  const [matterName, setMatterName] = useState("")

  // Data queries
  const { data: divisions = [], isLoading: divisionsLoading } = useDivisions()
  const { data: workflows = [], isLoading: workflowsLoading } = useWorkflows(selectedDivision?._id || null)
  const { data: workflowContacts = [], isLoading: contactsLoading } = useWorkflowContacts(selectedWorkflow?._id || null)
  const { data: workflowRoles = [], isLoading: rolesLoading } = useWorkflowRoles(selectedWorkflow?._id || null)

  // Get main contacts (required)
  const mainContacts = useMemo(
    () => workflowContacts.filter((c: WorkflowContact) => c.main_contact),
    [workflowContacts]
  )

  // Check if at least one main contact is assigned
  const hasMainContact = useMemo(() => {
    return mainContacts.some((c: WorkflowContact) => parties[c._id])
  }, [mainContacts, parties])

  // Step validation
  const canProceed = (): boolean => {
    switch (step) {
      case 1:
        return !!selectedDivision
      case 2:
        return !!selectedWorkflow
      case 3:
        return hasMainContact
      case 4:
        return true // Roles are optional
      case 5:
        return !!matterName.trim()
      default:
        return false
    }
  }

  // Reset form
  const resetForm = () => {
    setStep(1)
    setError(null)
    setSelectedDivision(null)
    setSelectedWorkflow(null)
    setParties({})
    setRoles({})
    setMatterName("")
  }

  const handleClose = () => {
    resetForm()
    onOpenChange(false)
  }

  // Step navigation
  const handleNext = () => {
    if (step < 5) {
      setStep((step + 1) as WizardStep)
    }
  }

  const handleBack = () => {
    if (step > 1) {
      setStep((step - 1) as WizardStep)
    }
  }

  // When division changes, reset workflow and downstream
  const handleDivisionSelect = (division: Division | null) => {
    setSelectedDivision(division)
    setSelectedWorkflow(null)
    setParties({})
    setRoles({})
    setMatterName("")
  }

  // When workflow changes, reset parties and roles
  const handleWorkflowSelect = (workflow: Workflow | null) => {
    setSelectedWorkflow(workflow)
    setParties({})
    setRoles({})
    setMatterName("")
  }

  // Update party assignment
  const handlePartyChange = (workflowContactId: string, contactId: string | null) => {
    setParties((prev) => {
      if (contactId) {
        return { ...prev, [workflowContactId]: contactId }
      } else {
        const { [workflowContactId]: _, ...rest } = prev
        return rest
      }
    })
  }

  // Update role assignment
  const handleRoleChange = (workflowRoleId: string, userId: string | null) => {
    setRoles((prev) => {
      if (userId) {
        return { ...prev, [workflowRoleId]: userId }
      } else {
        const { [workflowRoleId]: _, ...rest } = prev
        return rest
      }
    })
  }

  // Submit the form
  const handleSubmit = async () => {
    if (!selectedWorkflow || !selectedDivision || !matterName.trim()) {
      return
    }

    setError(null)

    // Build parties array
    const matterParties: CreateMatterParty[] = Object.entries(parties).map(([wcId, cId]) => ({
      workflow_contact: wcId,
      contact: cId,
    }))

    // Build roles array
    const matterRoles: CreateMatterRole[] = Object.entries(roles).map(([wrId, uId]) => ({
      workflow_role: wrId,
      user: uId,
    }))

    try {
      const result = await createMatter.mutateAsync({
        workflow: selectedWorkflow._id,
        division: selectedDivision._id,
        name: matterName.trim(),
        parties: matterParties,
        roles: matterRoles,
      })

      handleClose()
      // Navigate to the newly created matter
      if (result?.data?._id) {
        navigate(`/matters/${result.data._id}`)
      }
    } catch (err: any) {
      setError(err.message || "Failed to create matter")
    }
  }

  // Step titles
  const stepTitles: Record<WizardStep, string> = {
    1: "Select Division",
    2: "Select Workflow",
    3: "Assign Contacts",
    4: "Assign Roles",
    5: "Create Matter",
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Briefcase className="h-5 w-5" />
            New Matter
          </DialogTitle>
          <DialogDescription>
            Step {step} of 5: {stepTitles[step]}
          </DialogDescription>
        </DialogHeader>

        {/* Progress indicator */}
        <div className="flex items-center gap-1 py-2">
          {[1, 2, 3, 4, 5].map((s) => (
            <div
              key={s}
              className={cn(
                "flex-1 h-1.5 rounded-full transition-colors",
                s <= step ? "bg-primary" : "bg-muted"
              )}
            />
          ))}
        </div>

        {/* Step content */}
        <div className="flex-1 overflow-y-auto py-4 min-h-[200px]">
          {/* Step 1: Select Division */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select the division for this matter.
              </p>
              {divisionsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-2">
                  {divisions.map((division: Division) => (
                    <button
                      key={division._id}
                      type="button"
                      onClick={() => handleDivisionSelect(division)}
                      className={cn(
                        "w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left",
                        selectedDivision?._id === division._id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted"
                      )}
                    >
                      <span className="font-medium">{division.name}</span>
                      {selectedDivision?._id === division._id && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Select Workflow */}
          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Select the matter type (workflow) for this case.
              </p>
              {workflowsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : workflows.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No workflows found for this division.
                </p>
              ) : (
                <div className="space-y-2">
                  {workflows.map((workflow: Workflow) => (
                    <button
                      key={workflow._id}
                      type="button"
                      onClick={() => handleWorkflowSelect(workflow)}
                      className={cn(
                        "w-full flex items-center justify-between p-3 rounded-lg border transition-colors text-left",
                        selectedWorkflow?._id === workflow._id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted"
                      )}
                    >
                      <span className="font-medium">{workflow.name}</span>
                      {selectedWorkflow?._id === workflow._id && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 3: Assign Contacts */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Assign contacts to the party roles. At least one main contact is required.
              </p>
              {contactsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : workflowContacts.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No contact roles defined for this workflow.
                </p>
              ) : (
                <div className="space-y-4">
                  {workflowContacts.map((wc: WorkflowContact) => (
                    <div key={wc._id} className="space-y-1.5">
                      <Label className="flex items-center gap-2">
                        {wc.name}
                        {wc.main_contact && (
                          <span className="text-xs text-primary font-normal">(Required)</span>
                        )}
                      </Label>
                      <ContactSearch
                        value={parties[wc._id] || null}
                        onChange={(option) => handlePartyChange(wc._id, option?.value || null)}
                        placeholder={`Search for ${wc.name.toLowerCase()}...`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Assign Roles */}
          {step === 4 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Assign staff members to roles. This step is optional.
              </p>
              {rolesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : workflowRoles.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No staff roles defined for this workflow.
                </p>
              ) : (
                <div className="space-y-4">
                  {workflowRoles.map((wr: WorkflowRole) => (
                    <div key={wr._id} className="space-y-1.5">
                      <Label>{wr.name}</Label>
                      <UserSearch
                        value={roles[wr._id] || null}
                        onChange={(option) => handleRoleChange(wr._id, option?.value || null)}
                        placeholder={`Assign ${wr.name.toLowerCase()}...`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Finish */}
          {step === 5 && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Review your selections and enter a name for the matter.
              </p>

              {/* Summary */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between py-1 border-b">
                  <span className="text-muted-foreground">Division</span>
                  <span className="font-medium">{selectedDivision?.name}</span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-muted-foreground">Workflow</span>
                  <span className="font-medium">{selectedWorkflow?.name}</span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-muted-foreground">Contacts</span>
                  <span className="font-medium">{Object.keys(parties).length} assigned</span>
                </div>
                <div className="flex justify-between py-1 border-b">
                  <span className="text-muted-foreground">Roles</span>
                  <span className="font-medium">{Object.keys(roles).length} assigned</span>
                </div>
              </div>

              {/* Matter name input */}
              <div className="space-y-1.5 pt-2">
                <Label htmlFor="matterName">Matter Name *</Label>
                <Input
                  id="matterName"
                  value={matterName}
                  onChange={(e) => setMatterName(e.target.value)}
                  placeholder="Enter matter name..."
                  autoFocus
                />
              </div>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="text-sm text-danger bg-danger/10 px-3 py-2 rounded-md">
            {error}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0 border-t pt-4">
          {step > 1 && (
            <Button type="button" variant="outline" onClick={handleBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
          )}
          <div className="flex-1" />
          {step < 5 ? (
            <Button type="button" onClick={handleNext} disabled={!canProceed()}>
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!canProceed() || createMatter.isPending}
            >
              {createMatter.isPending && (
                <CircleNotch className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Matter
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
